import { useCallback } from "react"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { AxiosInstance } from "axios"
import { useUserStore } from "stores/userStore"
import createAuthenticatedAxios from "api/axiosInstance"
import { KindeRolesClaim } from "types/kinde.types"

/**
 * Returns a stable async getter for an authenticated axios instance.
 *
 * Auth (Kinde token + roles) is resolved lazily on each call, so the getter
 * can be closed over by react-query queryFn/mutationFn without creating
 * promises during render. This is the app's single auth bridge for API
 * calls (queries, mutations, and `useApiCommand` all build on it).
 *
 * Pass a baseURL for cross-server calls (superadmin) — defaults to the
 * current origin's API.
 *
 * @example
 * const getAxios = useAuthedAxios()
 * useQuery({ queryFn: async () => (await getProjects(await getAxios(), filter)).data })
 */
export const useAuthedAxios = (): ((
  baseURL?: string
) => Promise<AxiosInstance>) => {
  const { getAccessToken, getClaim } = useKindeAuth()
  const user = useUserStore((state) => state.user)

  return useCallback(async (baseURL?: string): Promise<AxiosInstance> => {
    const token = await getAccessToken()
    if (!token) {
      throw new Error("Authentication token not available")
    }

    const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
    if (!userRoles?.value?.[0]?.name) {
      throw new Error("User roles not available")
    }

    return createAuthenticatedAxios(
      token,
      user.username,
      user.unit,
      userRoles.value[0].name,
      baseURL
    )
  }, [getAccessToken, getClaim, user.username, user.unit])
}
